import { z } from 'zod/v4';
import { ObjectEntries } from '@/utils';

export type Override<T> = {
  /**
   * The value to match against the series name.
   */
  value: string;
  properties: T;
  matcher?: {
    type?: OverrideMatcherType;
  };
};

export const overrideMatcherTypeSchema = z.enum(['ByRegex', 'ByName']);
export type OverrideMatcherType = z.infer<typeof overrideMatcherTypeSchema>;

export const isRegexMatcher = (overrideMatcher?: OverrideMatcherType) =>
  overrideMatcher === overrideMatcherTypeSchema.enum.ByRegex;

/**
 * newOverrideObject is a helper function to create an override object with a given zod properties object.
 * This works in lieu of a generic class, as generic classes don't have any validation on them.
 *
 * @param properties is the zod override type that goes into the override object.
 * @returns an override object with a value, properties, and is_regex flag.
 */
export function newOverrideObject<T extends z.ZodTypeAny>(properties: T) {
  return z.object({
    value: z.string(),
    properties: properties,
    matcher: overrideMatcherSchema.optional(),
  });
}

const overrideMatcherSchema = z.object({
  type: overrideMatcherTypeSchema.optional(),
});

const DEFAULT_OVERRIDE_MATCHER = { type: overrideMatcherTypeSchema.enum.ByName };

/**
 * Valid colors when applying an override are hex, rgb, rgba, hsl, hsla
 * TODO: support predefined colors like 'red' and 'blue', map to chrono-ui theme tokens
 */
export interface ColorOverride extends z.TypeOf<typeof colorOverrideSchema> {}
export const colorOverrideSchema = z.object({
  color: z.string().optional().describe('Valid colors are hex, rgb, rgba, hsl, hsla'),
});

/**
 * Line style override to adjust line style of a series.
 * Valid options match the echarts type - https://echarts.apache.org/en/option.html#series-line.lineStyle.type
 */
export const lineStyleOverrideTypeSchema = z.enum(['Dashed', 'Solid']);
export type LineStyleOverrideType = z.infer<typeof lineStyleOverrideTypeSchema>;

export interface LineStyleOverride extends z.TypeOf<typeof lineStyleOverrideSchema> {}
const lineStyleOverrideSchema = z.object({
  line_style: lineStyleOverrideTypeSchema.optional().describe('Valid styles are Solid and Dashed'),
});

// VisualOverride extends a color override to adjust width of a series.
export interface VisualOverride extends z.TypeOf<typeof visualOverrideSchema> {}
export const visualOverrideSchema = z
  .object({
    // TODO: Add ability to override bar min width in the future
    line_width: z.number().optional(),
  })
  .merge(colorOverrideSchema)
  .merge(lineStyleOverrideSchema);

/**
 * Used to override series properties such as color when a match is found.
 * Value can be an exact series name or regex (with /example/ delimiter)
 */
export interface SeriesOverrides extends z.TypeOf<typeof seriesOverridesSchema> {}
export const seriesOverridesSchema = z.array(newOverrideObject(visualOverrideSchema));

export type GenericOverride<T> = {
  value: string;
  properties: T;
  matcher?: z.TypeOf<typeof overrideMatcherSchema>;
};

export type GenericOverrides<T> = Array<GenericOverride<T>>;

/**
 * Return valid regex from input string, similar approach in Grafana v7 lines below.
 * https://github.com/grafana/grafana/blob/v7.5.x/packages/grafana-data/src/text/string.ts#L20-L40
 */
export function parseOverrideAlias(alias: string) {
  // Confirm value is actually regex, just in case is_regex flag was set incorrectly
  if (!alias.startsWith('/')) {
    return new RegExp(`^${alias}$`);
  }

  const match = alias.match(OVERRIDES_DEFAULT_REGEX_PATTERN);

  if (!match) {
    throw new Error(`'${alias}' is not a valid regular expression.`);
  }

  const [, pattern, flags] = match;

  if (!pattern) {
    throw new Error(`'${pattern}' is not a valid regular expression pattern.`);
  }

  return new RegExp(pattern, flags);
}

/**
 * Determine whether to apply an override, taking into account whether value is an entire series name or if regex was passed.
 * If regex was passed, parse to determine if it is valid and if there is a match.
 */
export function matchSeriesOverride({
  value,
  name,
  isRegex = false,
}: {
  value: string;
  name: string;
  isRegex?: boolean;
}) {
  if (!value) {
    return false;
  }

  if (isRegex) {
    const regex = parseOverrideAlias(value);
    return regex.test(name);
  }

  // Check for series name and trim whitespace
  return value.trim() === name.trim();
}

/**
 * Returns the first override that positively matches against value
 *
 * @param value The string to try to match
 * @param overrides the array of overrides
 */
export function findMatchingOverride<T extends object>(value: string, overrides: GenericOverrides<T>) {
  return overrides.find((override) =>
    matchSeriesOverride({
      name: value,
      value: override.value,
      isRegex: isRegexMatcher(override.matcher?.type ?? overrideMatcherTypeSchema.enum.ByName),
    })
  );
}

/**
 * Apply override when match is found, currently only color is supported
 */
export function getOverriddenProperties<T extends object>(
  seriesName: string,
  overrides?: GenericOverrides<T>
): Partial<T> {
  const overriddenProperties: T = {} as T;
  if (!overrides) return overriddenProperties;
  for (const override of overrides) {
    const { value, properties } = override;
    const matcher = override.matcher ?? DEFAULT_OVERRIDE_MATCHER;
    if (matchSeriesOverride({ value, name: seriesName, isRegex: isRegexMatcher(matcher.type) })) {
      for (const [key, value] of Object.entries(properties) as ObjectEntries<T>) {
        overriddenProperties[key] = value;
      }
    }
  }
  return overriddenProperties;
}

/**
 * Match Grafana 7.5's default pattern to avoid discrepancies.
 * Flags stand for: global, ignore case, multiline, sticky (match from the current position in the target string)
 */
export const OVERRIDES_DEFAULT_REGEX_PATTERN = new RegExp('^/(.*?)/(g?i?m?y?)$');

export const OVERRIDES_EDITOR_WIDTH = 250;
